import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatBytes(bytes, precision = 1) {
    if (!bytes || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    while (bytes >= 1024 && i < units.length - 1) { bytes /= 1024; i++; }
    return `${parseFloat(bytes.toFixed(precision))} ${units[i]}`;
}

function formatDate(dateString) {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
    });
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const TypeBadge = ({ type }) => (
    <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded ${
        type === 'blog'
            ? 'bg-blue-100 text-blue-700'
            : 'bg-purple-100 text-purple-700'
    }`}>
        {type === 'blog' ? 'Blog' : 'Profile'}
    </span>
);

// Skeleton card for grid view initial-load state
const SkeletonCard = () => (
    <div className="animate-pulse bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
        <div className="aspect-square bg-gray-200" />
        <div className="p-2 space-y-1.5">
            <div className="h-3 bg-gray-200 rounded w-10" />
            <div className="h-3 bg-gray-200 rounded w-full" />
            <div className="h-2.5 bg-gray-200 rounded w-16" />
        </div>
    </div>
);

// Skeleton rows for list view initial-load state
const SkeletonListRows = ({ count = 8 }) => (
    <>
        {Array.from({ length: count }).map((_, i) => (
            <tr key={i} className="animate-pulse">
                <td className="px-4 py-3"><div className="w-12 h-12 bg-gray-200 rounded" /></td>
                <td className="px-4 py-3">
                    <div className="h-3.5 bg-gray-200 rounded w-36 mb-1.5" />
                    <div className="h-3 bg-gray-200 rounded w-24" />
                </td>
                <td className="px-4 py-3"><div className="h-5 bg-gray-200 rounded w-12" /></td>
                <td className="px-4 py-3"><div className="h-3.5 bg-gray-200 rounded w-12" /></td>
                <td className="px-4 py-3"><div className="h-3.5 bg-gray-200 rounded w-20" /></td>
                <td className="px-4 py-3"><div className="h-3.5 bg-gray-200 rounded w-20" /></td>
                <td className="px-4 py-3"><div className="h-3.5 bg-gray-200 rounded w-16" /></td>
                <td className="px-4 py-3"><div className="h-3.5 bg-gray-200 rounded w-14" /></td>
                <td className="px-4 py-3"><div className="h-3.5 bg-gray-200 rounded w-16 ml-auto" /></td>
            </tr>
        ))}
    </>
);

// Image card for grid view
const ImageCard = ({ image, dims, onImageLoad, onPreview, onDelete, onReplace }) => (
    <div className="group relative bg-gray-50 rounded-lg border border-gray-200 overflow-hidden hover:border-red-200 hover:shadow-md transition-all duration-150">
        {/* Thumbnail */}
        <div
            className="relative aspect-square bg-gray-100 overflow-hidden cursor-pointer"
            onClick={onPreview}
        >
            <img
                ref={el => { if (el && el.complete && el.naturalWidth > 0) onImageLoad(image.id, { target: el }); }}
                src={image.url}
                alt={image.original_filename}
                className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                onLoad={(e) => onImageLoad(image.id, e)}
                loading="lazy"
            />
            {/* Hover action overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-all duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                <button
                    onClick={(e) => { e.stopPropagation(); onPreview(); }}
                    className="p-2 bg-white rounded-full text-gray-700 hover:text-blue-600 shadow transition-colors"
                    title="Preview"
                >
                    <FontAwesomeIcon icon="eye" className="w-3.5 h-3.5" />
                </button>
                <button
                    onClick={(e) => { e.stopPropagation(); onReplace(); }}
                    className="p-2 bg-white rounded-full text-gray-700 hover:text-green-600 shadow transition-colors"
                    title="Replace image"
                >
                    <FontAwesomeIcon icon="arrows-rotate" className="w-3.5 h-3.5" />
                </button>
                <button
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    className="p-2 bg-white rounded-full text-gray-700 hover:text-red-600 shadow transition-colors"
                    title="Delete image"
                >
                    <FontAwesomeIcon icon="trash" className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>

        {/* Metadata strip */}
        <div className="p-2">
            <div className="flex items-center justify-between mb-1">
                <TypeBadge type={image.type} />
                {image.usage_count > 0 && (
                    <span className="text-xs text-gray-400" title={`Used in ${image.usage_count} ${image.type === 'blog' ? 'post(s)' : 'profile'}`}>
                        <FontAwesomeIcon icon="link" className="mr-0.5" />
                        {image.usage_count}
                    </span>
                )}
            </div>
            <p className="text-xs text-gray-800 font-medium truncate leading-tight" title={image.original_filename}>
                {image.original_filename}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
                {image.file_size_formatted}
                {dims && ` · ${dims.width}×${dims.height}`}
            </p>
        </div>
    </div>
);

// List / table view
const ImageListView = ({ images, dimensions, onImageLoad, onPreview, onDelete, onReplace }) => (
    <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
                <tr>
                    {['Image', 'Filename', 'Type', 'Size', 'Dimensions', 'Uploaded', 'Uploader', 'Usage', ''].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                            {h}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
                {images.map(image => {
                    const dims = dimensions[image.id];
                    return (
                        <tr key={image.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3">
                                <div
                                    className="w-12 h-12 rounded border border-gray-200 overflow-hidden cursor-pointer flex-shrink-0 bg-gray-50 hover:opacity-80 transition-opacity"
                                    onClick={() => onPreview(image)}
                                >
                                    <img
                                        ref={el => { if (el && el.complete && el.naturalWidth > 0) onImageLoad(image.id, { target: el }); }}
                                        src={image.url}
                                        alt={image.original_filename}
                                        className="w-full h-full object-cover"
                                        onLoad={(e) => onImageLoad(image.id, e)}
                                    />
                                </div>
                            </td>
                            <td className="px-4 py-3 max-w-xs">
                                <p className="text-sm font-medium text-gray-900 truncate" title={image.original_filename}>
                                    {image.original_filename}
                                </p>
                                {image.original_filename !== image.filename && (
                                    <p className="text-xs text-gray-400 truncate" title={image.filename}>
                                        {image.filename}
                                    </p>
                                )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                                <TypeBadge type={image.type} />
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                {image.file_size_formatted}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                {dims ? `${dims.width} × ${dims.height}` : <span className="text-gray-300">-</span>}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                {formatDate(image.created_at)}
                            </td>
                            <td className="px-4 py-3 text-sm whitespace-nowrap">
                                {image.type === 'profile' ? (
                                    <Link
                                        to={`/profile/${image.uploader_id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline"
                                    >
                                        @{image.uploader_username}
                                    </Link>
                                ) : (
                                    <span className="text-gray-600">@{image.uploader_username}</span>
                                )}
                            </td>
                            <td className="px-4 py-3 text-sm whitespace-nowrap">
                                {image.type === 'blog' ? (
                                    <span className={image.usage_count > 0 ? 'text-green-600' : 'text-gray-400'}>
                                        {image.usage_count} post{image.usage_count !== 1 ? 's' : ''}
                                    </span>
                                ) : (
                                    <span className="text-purple-600">
                                        @{image.user_username}
                                    </span>
                                )}
                            </td>
                            <td className="px-4 py-3 text-right whitespace-nowrap">
                                <div className="flex items-center justify-end gap-3">
                                    <button
                                        onClick={() => onPreview(image)}
                                        className="text-gray-400 hover:text-blue-600 transition-colors"
                                        title="Preview"
                                    >
                                        <FontAwesomeIcon icon="eye" />
                                    </button>
                                    <button
                                        onClick={() => onReplace(image)}
                                        className="text-gray-400 hover:text-green-600 transition-colors"
                                        title="Replace"
                                    >
                                        <FontAwesomeIcon icon="arrows-rotate" />
                                    </button>
                                    <button
                                        onClick={() => onDelete(image)}
                                        className="text-gray-400 hover:text-red-600 transition-colors"
                                        title="Delete"
                                    >
                                        <FontAwesomeIcon icon="trash" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </div>
);

// Preview modal
const PreviewModal = ({ image, dims, onClose, onDelete, onReplace }) => (
    <div
        className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4"
        onClick={onClose}
    >
        <div
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
        >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-base font-semibold text-gray-900 truncate mr-4" title={image.original_filename}>
                    {image.original_filename}
                </h3>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0">
                    <FontAwesomeIcon icon="times" className="text-xl" />
                </button>
            </div>

            {/* Body */}
            <div className="flex flex-col md:flex-row overflow-hidden flex-1">
                {/* Image */}
                <div className="flex-1 bg-gray-100 flex items-center justify-center p-6 min-h-48 overflow-hidden">
                    <img
                        src={image.url}
                        alt={image.original_filename}
                        className="max-w-full max-h-[60vh] object-contain rounded shadow-sm"
                    />
                </div>

                {/* Details panel */}
                <div className="w-full md:w-72 flex-shrink-0 border-t md:border-t-0 md:border-l border-gray-200 overflow-y-auto">
                    <dl className="divide-y divide-gray-100 text-sm">
                        {[
                            { label: 'Type',     value: <TypeBadge type={image.type} /> },
                            { label: 'Format',   value: image.mime_type },
                            { label: 'Size',     value: image.file_size_formatted },
                            { label: 'Dimensions', value: dims ? `${dims.width} × ${dims.height} px` : 'Loading…' },
                            { label: 'Uploaded', value: formatDate(image.created_at) },
                            { label: 'By',       value: `@${image.uploader_username}` },
                            ...(image.type === 'blog' ? [
                                { label: 'Used in', value: `${image.usage_count} post${image.usage_count !== 1 ? 's' : ''}` }
                            ] : []),
                            ...(image.type === 'profile' ? [
                                { label: 'Profile', value: `@${image.user_username}` }
                            ] : []),
                            { label: 'Stored as', value: <span className="break-all text-xs text-gray-500">{image.filename}</span> },
                        ].map(({ label, value }) => (
                            <div key={label} className="px-5 py-3">
                                <dt className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">{label}</dt>
                                <dd className="text-gray-900">{value}</dd>
                            </div>
                        ))}
                    </dl>

                    <div className="px-5 py-4 border-t border-gray-100">
                        <a
                            href={image.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                        >
                            <FontAwesomeIcon icon="arrow-right" />
                            Open original in S3
                        </a>
                    </div>

                    {/* Actions */}
                    <div className="px-5 pb-5 flex flex-col gap-2">
                        <button
                            onClick={() => { onClose(); onReplace(image); }}
                            className="w-full px-3 py-2 text-sm font-medium bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 hover:border-green-400 hover:text-green-700 transition-colors flex items-center justify-center gap-2"
                        >
                            <FontAwesomeIcon icon="arrows-rotate" />
                            Replace Image
                        </button>
                        <button
                            onClick={() => { onClose(); onDelete(image); }}
                            className="w-full px-3 py-2 text-sm font-medium bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors flex items-center justify-center gap-2"
                        >
                            <FontAwesomeIcon icon="trash" />
                            Delete Image
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

// Delete confirmation modal
const DeleteModal = ({ image, isDeleting, onConfirm, onClose }) => {
    const isBlogWithUsage = image.type === 'blog' && image.usage_count > 0;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
                <div className="p-6">
                    <div className="flex items-start gap-4 mb-5">
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${isBlogWithUsage ? 'bg-red-100' : 'bg-yellow-100'}`}>
                            <FontAwesomeIcon
                                icon="circle-exclamation"
                                className={isBlogWithUsage ? 'text-red-600' : 'text-yellow-600'}
                            />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Delete Image</h3>
                            <p className="text-sm text-gray-500 mt-0.5 break-all">{image.original_filename}</p>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {/* Usage warnings */}
                        {isBlogWithUsage && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                                <FontAwesomeIcon icon="circle-exclamation" className="mr-2" />
                                This image is referenced in{' '}
                                <strong>{image.usage_count} blog post{image.usage_count !== 1 ? 's' : ''}</strong>.
                                Those references will be removed automatically.
                            </div>
                        )}
                        {image.type === 'profile' && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-700">
                                <FontAwesomeIcon icon="user" className="mr-2" />
                                This is the profile picture for <strong>@{image.user_username}</strong>.
                                Their profile will have no avatar after deletion.
                            </div>
                        )}
                        {image.type === 'blog' && image.usage_count === 0 && (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600">
                                This image is not referenced by any blog posts.
                            </div>
                        )}

                        {/* What will happen */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600 space-y-1">
                            <p className="flex items-center gap-2">
                                <FontAwesomeIcon icon="trash" className="text-red-400 w-3.5" />
                                Permanently deleted from R2 storage
                            </p>
                            {image.type === 'blog' && image.usage_count > 0 && (
                                <p className="flex items-center gap-2">
                                    <FontAwesomeIcon icon="edit" className="text-orange-400 w-3.5" />
                                    Featured image &amp; inline references cleared from {image.usage_count} post{image.usage_count !== 1 ? 's' : ''}
                                </p>
                            )}
                            {image.type === 'profile' && (
                                <p className="flex items-center gap-2">
                                    <FontAwesomeIcon icon="user" className="text-orange-400 w-3.5" />
                                    Profile picture removed for @{image.user_username}
                                </p>
                            )}
                        </div>

                        <p className="text-xs text-gray-400">This action cannot be undone.</p>
                    </div>

                    <div className="mt-6 flex gap-3 justify-end">
                        <button
                            onClick={onClose}
                            disabled={isDeleting}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={isDeleting}
                            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                        >
                            {isDeleting ? (
                                <>
                                    <FontAwesomeIcon icon="spinner" className="animate-spin" />
                                    Deleting…
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon="trash" />
                                    Delete Permanently
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Replace image modal
const ReplaceModal = ({ image, newFile, preview, isReplacing, onFileChange, onConfirm, onClose }) => {
    const fileInputRef = useRef(null);

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Replace Image</h3>
                    <button
                        onClick={onClose}
                        disabled={isReplacing}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-50 transition-colors"
                    >
                        <FontAwesomeIcon icon="times" />
                    </button>
                </div>

                <div className="p-5 space-y-5">
                    {/* Before / After preview */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Current</p>
                            <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                                <img src={image.url} alt="Current" className="w-full h-full object-cover" />
                            </div>
                            <p className="text-xs text-gray-400 mt-1 truncate" title={image.original_filename}>
                                {image.original_filename}
                            </p>
                            <p className="text-xs text-gray-400">{image.file_size_formatted}</p>
                        </div>
                        <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Replacement</p>
                            <div
                                className={`aspect-square rounded-lg overflow-hidden border-2 border-dashed cursor-pointer flex items-center justify-center transition-colors ${
                                    preview
                                        ? 'border-green-400 bg-green-50'
                                        : 'border-gray-300 bg-gray-50 hover:border-red-400 hover:bg-red-50'
                                }`}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {preview ? (
                                    <img src={preview} alt="New" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="text-center p-4">
                                        <FontAwesomeIcon icon="cloud-arrow-up" className="text-3xl text-gray-300 mb-2" />
                                        <p className="text-xs text-gray-400">Click to select</p>
                                        <p className="text-xs text-gray-300">JPG, PNG, GIF, WebP</p>
                                    </div>
                                )}
                            </div>
                            {newFile ? (
                                <>
                                    <p className="text-xs text-gray-700 mt-1 truncate font-medium" title={newFile.name}>
                                        {newFile.name}
                                    </p>
                                    <p className="text-xs text-gray-400">{formatBytes(newFile.size)}</p>
                                </>
                            ) : (
                                <p className="text-xs text-gray-400 mt-1">No file selected</p>
                            )}
                        </div>
                    </div>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={onFileChange}
                        className="hidden"
                    />

                    {/* What will happen info box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1.5 text-sm text-blue-700">
                        <p className="font-semibold text-blue-800">What will happen:</p>
                        <ul className="space-y-1 text-blue-600">
                            <li className="flex items-center gap-2">
                                <FontAwesomeIcon icon="check" className="text-blue-500 w-3.5 flex-shrink-0" />
                                Old image permanently removed from R2
                            </li>
                            <li className="flex items-center gap-2">
                                <FontAwesomeIcon icon="check" className="text-blue-500 w-3.5 flex-shrink-0" />
                                New image uploaded to R2
                            </li>
                            {image.type === 'blog' && image.usage_count > 0 && (
                                <li className="flex items-center gap-2">
                                    <FontAwesomeIcon icon="check" className="text-blue-500 w-3.5 flex-shrink-0" />
                                    All {image.usage_count} reference{image.usage_count !== 1 ? 's' : ''} in blog posts updated automatically
                                </li>
                            )}
                            {image.type === 'profile' && (
                                <li className="flex items-center gap-2">
                                    <FontAwesomeIcon icon="check" className="text-blue-500 w-3.5 flex-shrink-0" />
                                    Profile picture for @{image.user_username} updated
                                </li>
                            )}
                        </ul>
                    </div>
                </div>

                <div className="px-5 pb-5 flex gap-3 justify-end">
                    <button
                        onClick={onClose}
                        disabled={isReplacing}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isReplacing || !newFile}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                        {isReplacing ? (
                            <>
                                <FontAwesomeIcon icon="spinner" className="animate-spin" />
                                Replacing…
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon="arrows-rotate" />
                                Replace Image
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const AdminFileManager = () => {
    const { user, isLoading: authLoading, checkAdminStatus } = useAuth();
    const navigate = useNavigate();
    useDocumentTitle('Admin File Manager');

    const [isLoading, setIsLoading]     = useState(true);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isFetching, setIsFetching]   = useState(false);
    const [error, setError]             = useState(null);

    const [images, setImages]           = useState([]);
    const [stats, setStats]             = useState({
        total_images: 0, total_size_formatted: '0 B', blog_images: 0, profile_images: 0
    });
    const [pagination, setPagination]   = useState({
        current_page: 1, total_pages: 1, total_images: 0, images_per_page: 24
    });
    const [filters, setFilters]         = useState({
        type: 'all', sort: 'date_desc', search: ''
    });
    const [viewMode, setViewMode]       = useState('grid');
    const [dimensions, setDimensions]   = useState({});
    // True after the very first successful load - used to distinguish initial-load skeleton
    // from filter-change refetch (where we keep showing existing images)
    const hasLoaded = useRef(false);

    // Modals
    const [previewImage, setPreviewImage] = useState(null);
    const [deleteModal, setDeleteModal]   = useState({ show: false, image: null, isDeleting: false });
    const [replaceModal, setReplaceModal] = useState({ show: false, image: null, isReplacing: false, newFile: null, preview: null });

    // ─── Auth check ─────────────────────────────────────────────────────────
    useEffect(() => {
        const verifyAdmin = async () => {
            if (authLoading) return;
            if (!user) { navigate('/'); return; }
            const isAdmin = await checkAdminStatus();
            if (!isAdmin) { navigate('/'); return; }
            setIsAuthorized(true);
            setIsLoading(false);
        };
        verifyAdmin();
    }, [user, authLoading, checkAdminStatus, navigate]);

    // ─── Load images ─────────────────────────────────────────────────────────
    const loadImages = async () => {
        if (!isAuthorized) return;
        setIsFetching(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                type:  filters.type,
                sort:  filters.sort,
                page:  pagination.current_page,
                limit: pagination.images_per_page,
            });
            if (filters.search) params.append('search', filters.search);

            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/admin_file_manager_list.php?${params}`,
                { credentials: 'include' }
            );
            if (!res.ok) throw new Error('Failed to fetch images');
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Failed to load images');

            setImages(data.images);
            setStats(data.stats);
            setPagination(data.pagination);
            setDimensions({});
            hasLoaded.current = true;
        } catch (err) {
            setError(err.message);
        } finally {
            setIsFetching(false);
        }
    };

    useEffect(() => {
        if (isAuthorized) loadImages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthorized, filters, pagination.current_page]);

    // ─── Filter helpers ──────────────────────────────────────────────────────
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPagination(prev => ({ ...prev, current_page: 1 }));
    };

    // useCallback so the ref callbacks in ImageCard/ImageListView get a stable reference.
    // The functional setState with early-return bail-out prevents a re-render cascade:
    // the ref callback fires on every render, but after the first successful set,
    // returning the same prev object means React skips the re-render.
    const handleImageLoad = useCallback((imageId, e) => {
        const { naturalWidth, naturalHeight } = e.target;
        if (!naturalWidth || !naturalHeight) return;
        setDimensions(prev => {
            if (prev[imageId]) return prev; // already known - no state change, no re-render
            return { ...prev, [imageId]: { width: naturalWidth, height: naturalHeight } };
        });
    }, []);

    // ─── Delete ──────────────────────────────────────────────────────────────
    const handleDeleteClick = (image) => {
        setDeleteModal({ show: true, image, isDeleting: false });
    };

    const handleDeleteConfirm = async () => {
        const { image } = deleteModal;
        setDeleteModal(prev => ({ ...prev, isDeleting: true }));
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/admin_file_manager_delete.php`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ type: image.type, db_id: image.db_id }),
                }
            );
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Delete failed');
            setDeleteModal({ show: false, image: null, isDeleting: false });
            await loadImages();
        } catch (err) {
            setError(err.message);
            setDeleteModal({ show: false, image: null, isDeleting: false });
        }
    };

    // ─── Replace ─────────────────────────────────────────────────────────────
    const handleReplaceClick = (image) => {
        setReplaceModal({ show: true, image, isReplacing: false, newFile: null, preview: null });
    };

    const handleReplaceFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            setReplaceModal(prev => ({ ...prev, newFile: file, preview: reader.result }));
        };
        reader.readAsDataURL(file);
    };

    const handleReplaceConfirm = async () => {
        const { image, newFile } = replaceModal;
        if (!newFile) return;
        setReplaceModal(prev => ({ ...prev, isReplacing: true }));
        try {
            const formData = new FormData();
            formData.append('type', image.type);
            formData.append('db_id', image.db_id);
            formData.append('image', newFile);

            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/admin_file_manager_replace.php`,
                { method: 'POST', credentials: 'include', body: formData }
            );
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Replace failed');
            setReplaceModal({ show: false, image: null, isReplacing: false, newFile: null, preview: null });
            await loadImages();
        } catch (err) {
            setError(err.message);
            setReplaceModal({ show: false, image: null, isReplacing: false, newFile: null, preview: null });
        }
    };

    // ─── Loading / auth gate ─────────────────────────────────────────────────
    if (isLoading || authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4" />
                    <p className="text-gray-500">Verifying admin access…</p>
                </div>
            </div>
        );
    }

    if (!isAuthorized) return null;

    // ─── Render ──────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* Breadcrumb */}
                <nav className="flex mb-6" aria-label="Breadcrumb">
                    <ol className="inline-flex items-center space-x-1 md:space-x-3">
                        <li className="inline-flex items-center">
                            <Link to="/admin" className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-red-600">
                                <FontAwesomeIcon icon="house" className="w-4 h-4 mr-2" />
                                Admin Dashboard
                            </Link>
                        </li>
                        <li>
                            <div className="flex items-center">
                                <FontAwesomeIcon icon="chevron-right" className="w-6 h-6 text-gray-400" />
                                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">File Manager</span>
                            </div>
                        </li>
                    </ol>
                </nav>

                {/* Page header */}
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">File Manager</h1>
                    <p className="mt-1 text-gray-500">
                        Manage images uploaded for blog posts and user profile pictures.
                        Delete oversized files or replace images - all references update automatically.
                    </p>
                </div>

                {/* Error banner */}
                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                        <FontAwesomeIcon icon="circle-exclamation" className="text-red-500 mt-0.5 flex-shrink-0" />
                        <p className="flex-1 text-sm text-red-700">{error}</p>
                        <button
                            onClick={() => setError(null)}
                            className="text-red-400 hover:text-red-600 flex-shrink-0 transition-colors"
                        >
                            <FontAwesomeIcon icon="times" />
                        </button>
                    </div>
                )}

                {/* Stats cards */}
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 mb-6">
                    {[
                        { label: 'Total Images',     value: stats.total_images },
                        { label: 'Storage Used',     value: stats.total_size_formatted },
                        { label: 'Blog Images',      value: stats.blog_images },
                        { label: 'Profile Pictures', value: stats.profile_images },
                    ].map(s => (
                        <div key={s.label} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                            <p className="text-xs font-medium text-gray-500 truncate">{s.label}</p>
                            {!hasLoaded.current && isFetching ? (
                                <div className="animate-pulse mt-2 h-7 w-16 bg-gray-200 rounded" />
                            ) : (
                                <p className="mt-1 text-2xl font-semibold text-gray-900">{s.value}</p>
                            )}
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div className="bg-white border border-gray-200 shadow-sm rounded-lg mb-6">
                    <div className="px-5 py-4 border-b border-gray-100">
                        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Filters</h2>
                    </div>
                    <div className="px-5 py-4">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                            {/* Type filter */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Image Type</label>
                                <select
                                    value={filters.type}
                                    onChange={e => handleFilterChange('type', e.target.value)}
                                    className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                >
                                    <option value="all">All Images</option>
                                    <option value="blog">Blog Images</option>
                                    <option value="profile">Profile Pictures</option>
                                </select>
                            </div>

                            {/* Sort */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                                <select
                                    value={filters.sort}
                                    onChange={e => handleFilterChange('sort', e.target.value)}
                                    className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                >
                                    <option value="date_desc">Newest First</option>
                                    <option value="date_asc">Oldest First</option>
                                    <option value="size_desc">Largest First</option>
                                    <option value="size_asc">Smallest First</option>
                                    <option value="name_asc">Filename A–Z</option>
                                </select>
                            </div>

                            {/* View toggle */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">View</label>
                                <div className="flex rounded-md border border-gray-300 overflow-hidden">
                                    <button
                                        onClick={() => setViewMode('grid')}
                                        className={`flex-1 px-3 py-2 text-sm flex items-center justify-center gap-1.5 transition-colors ${
                                            viewMode === 'grid'
                                                ? 'bg-red-600 text-white'
                                                : 'bg-white text-gray-700 hover:bg-gray-50'
                                        }`}
                                    >
                                        <FontAwesomeIcon icon="th" />
                                        Grid
                                    </button>
                                    <button
                                        onClick={() => setViewMode('list')}
                                        className={`flex-1 px-3 py-2 text-sm flex items-center justify-center gap-1.5 border-l border-gray-300 transition-colors ${
                                            viewMode === 'list'
                                                ? 'bg-red-600 text-white'
                                                : 'bg-white text-gray-700 hover:bg-gray-50'
                                        }`}
                                    >
                                        <FontAwesomeIcon icon="list" />
                                        List
                                    </button>
                                </div>
                            </div>

                            {/* Search */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                                <input
                                    type="text"
                                    value={filters.search}
                                    onChange={e => handleFilterChange('search', e.target.value)}
                                    placeholder="Search by filename…"
                                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Images panel */}
                <div className="bg-white border border-gray-200 shadow-sm rounded-lg">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Images</h2>
                        <div className="flex items-center gap-3">
                            {isFetching && hasLoaded.current && (
                                <FontAwesomeIcon icon="spinner" className="animate-spin text-gray-400" />
                            )}
                            <span className="text-sm text-gray-500">
                                {!hasLoaded.current && isFetching
                                    ? 'Loading…'
                                    : `Showing ${images.length} of ${pagination.total_images}`}
                            </span>
                        </div>
                    </div>

                    {/* Initial skeleton - shown only before the first load completes */}
                    {!hasLoaded.current && isFetching ? (
                        viewMode === 'grid' ? (
                            <div className="p-5 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                                {Array.from({ length: 12 }).map((_, i) => <SkeletonCard key={i} />)}
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            {['Image', 'Filename', 'Type', 'Size', 'Dimensions', 'Uploaded', 'Uploader', 'Usage', ''].map(h => (
                                                <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-100">
                                        <SkeletonListRows count={8} />
                                    </tbody>
                                </table>
                            </div>
                        )
                    ) : images.length === 0 && !isFetching ? (
                        <div className="text-center py-16">
                            <FontAwesomeIcon icon="images" className="text-gray-300 text-5xl mb-4" />
                            <p className="text-gray-500 font-medium">No images found</p>
                            <p className="text-gray-400 text-sm mt-1">
                                {filters.search ? 'Try a different search term.' : 'No images have been uploaded yet.'}
                            </p>
                        </div>
                    ) : viewMode === 'grid' ? (
                        <div className={`p-5 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 transition-opacity duration-150 ${isFetching ? 'opacity-50' : 'opacity-100'}`}>
                            {images.map(image => (
                                <ImageCard
                                    key={image.id}
                                    image={image}
                                    dims={dimensions[image.id]}
                                    onImageLoad={handleImageLoad}
                                    onPreview={() => setPreviewImage(image)}
                                    onDelete={() => handleDeleteClick(image)}
                                    onReplace={() => handleReplaceClick(image)}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className={`transition-opacity duration-150 ${isFetching ? 'opacity-50' : 'opacity-100'}`}>
                            <ImageListView
                                images={images}
                                dimensions={dimensions}
                                onImageLoad={handleImageLoad}
                                onPreview={setPreviewImage}
                                onDelete={handleDeleteClick}
                                onReplace={handleReplaceClick}
                            />
                        </div>
                    )}

                    {/* Pagination */}
                    {pagination.total_pages > 1 && (
                        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between">
                            <p className="text-sm text-gray-700">
                                Page <span className="font-medium">{pagination.current_page}</span> of{' '}
                                <span className="font-medium">{pagination.total_pages}</span>
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setPagination(prev => ({ ...prev, current_page: Math.max(1, prev.current_page - 1) }))}
                                    disabled={pagination.current_page === 1}
                                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                                >
                                    <FontAwesomeIcon icon="arrow-left" />
                                    Previous
                                </button>
                                <button
                                    onClick={() => setPagination(prev => ({ ...prev, current_page: Math.min(prev.total_pages, prev.current_page + 1) }))}
                                    disabled={pagination.current_page === pagination.total_pages}
                                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                                >
                                    Next
                                    <FontAwesomeIcon icon="arrow-right" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            {previewImage && (
                <PreviewModal
                    image={previewImage}
                    dims={dimensions[previewImage.id]}
                    onClose={() => setPreviewImage(null)}
                    onDelete={(img) => { setPreviewImage(null); handleDeleteClick(img); }}
                    onReplace={(img) => { setPreviewImage(null); handleReplaceClick(img); }}
                />
            )}

            {deleteModal.show && deleteModal.image && (
                <DeleteModal
                    image={deleteModal.image}
                    isDeleting={deleteModal.isDeleting}
                    onConfirm={handleDeleteConfirm}
                    onClose={() => setDeleteModal({ show: false, image: null, isDeleting: false })}
                />
            )}

            {replaceModal.show && replaceModal.image && (
                <ReplaceModal
                    image={replaceModal.image}
                    newFile={replaceModal.newFile}
                    preview={replaceModal.preview}
                    isReplacing={replaceModal.isReplacing}
                    onFileChange={handleReplaceFileChange}
                    onConfirm={handleReplaceConfirm}
                    onClose={() => setReplaceModal({ show: false, image: null, isReplacing: false, newFile: null, preview: null })}
                />
            )}
        </div>
    );
};

export default AdminFileManager;
